import { useEffect, useRef, useState } from "react";
import { FaImage, FaTimes, FaVideo } from "react-icons/fa";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { submitPortalReview } from "../../store/allocation/portalReviewSlice";
import { useToast } from "../utilities/Toast";

const MAX_SCREENSHOTS = 5;
const MAX_VIDEOS = 2;
const MAX_FILE_BYTES = 32 * 1024 * 1024;

type Props = {
  open: boolean;
  onClose: () => void;
};

function isImage(file: File) {
  return file.type.startsWith("image/") || /\.(jpe?g|png|webp|gif|avif)$/i.test(file.name);
}

function isVideo(file: File) {
  return file.type.startsWith("video/") || /\.(mp4|webm|mov|m4v)$/i.test(file.name);
}

function fileTooBig(file: File) {
  return file.size > MAX_FILE_BYTES;
}

const ReviewSubmitModal = ({ open, onClose }: Props) => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const submitting = useAppSelector((state) => state.portalReviews.submitting);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [screenshots, setScreenshots] = useState<File[]>([]);
  const [videos, setVideos] = useState<File[]>([]);
  const [screenshotPreviews, setScreenshotPreviews] = useState<string[]>([]);
  const screenshotInput = useRef<HTMLInputElement>(null);
  const videoInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const urls = screenshots.map((file) => URL.createObjectURL(file));
    setScreenshotPreviews(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [screenshots]);

  useEffect(() => {
    if (!open) return;

    const onPaste = (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) return;
      const pasted: File[] = [];
      for (const item of Array.from(items)) {
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) pasted.push(file);
        }
      }
      if (!pasted.length) return;
      event.preventDefault();
      setScreenshots((prev) => {
        const next = [...prev];
        for (const file of pasted) {
          if (fileTooBig(file)) {
            showToast("Each screenshot must be 32MB or smaller", "error");
            continue;
          }
          if (next.length < MAX_SCREENSHOTS) next.push(file);
        }
        return next;
      });
    };

    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [open, showToast]);

  if (!open) return null;

  const reset = () => {
    setTitle("");
    setMessage("");
    setScreenshots([]);
    setVideos([]);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const addScreenshots = (files: FileList | File[]) => {
    const incoming = Array.from(files).filter((file) => {
      if (!isImage(file)) {
        showToast("Please choose image files for screenshots", "error");
        return false;
      }
      if (fileTooBig(file)) {
        showToast("Each screenshot must be 32MB or smaller", "error");
        return false;
      }
      return true;
    });
    setScreenshots((prev) => [...prev, ...incoming].slice(0, MAX_SCREENSHOTS));
  };

  const addVideos = (files: FileList | File[]) => {
    const incoming = Array.from(files).filter((file) => {
      if (!isVideo(file)) {
        showToast("Please choose a video file", "error");
        return false;
      }
      if (fileTooBig(file)) {
        showToast("Each video must be 32MB or smaller", "error");
        return false;
      }
      return true;
    });
    setVideos((prev) => [...prev, ...incoming].slice(0, MAX_VIDEOS));
  };

  const handleSubmit = async () => {
    if (!message.trim()) {
      showToast("Please write what you want to share", "error");
      return;
    }
    try {
      await dispatch(
        submitPortalReview({
          title: title.trim(),
          message: message.trim(),
          screenshots,
          videos,
        }),
      ).unwrap();
      showToast("Feedback shared with Super Admin", "success");
      reset();
      onClose();
    } catch (err) {
      showToast(typeof err === "string" ? err : "Failed to submit feedback", "error");
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto border border-[#E8E8E4]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8E8E4]">
          <div>
            <h2 className="text-base font-semibold text-[#4A4A48]">Share feedback</h2>
            <p className="text-xs text-[#8A8A85] mt-0.5">
              Send updates, requests, a screenshot, or a short video. Super Admin will look at it.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-[#8A8A85] hover:text-[#4A4A48] p-1"
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <input
            type="text"
            value={title}
            maxLength={128}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title (optional)"
            className="w-full rounded-xl border border-[#DDDDD8] bg-[#F8F8F6] px-3 py-2.5 text-sm text-[#4A4A48] outline-none focus:border-[#2fa9bf] focus:ring-2 focus:ring-[#2fa9bf]/20"
          />
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="What should we know or improve in the portal?"
            className="w-full min-h-[140px] rounded-xl border border-[#DDDDD8] bg-[#F8F8F6] px-3 py-2.5 text-sm text-[#4A4A48] outline-none focus:border-[#2fa9bf] focus:ring-2 focus:ring-[#2fa9bf]/20"
          />

          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-xl border border-dashed border-[#DDDDD8] bg-[#F8F8F6] p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-[#4A4A48] inline-flex items-center gap-1.5">
                  <FaImage className="text-[#2fa9bf]" /> Screenshot
                </span>
                <span className="text-[11px] text-[#8A8A85]">
                  {screenshots.length}/{MAX_SCREENSHOTS}
                </span>
              </div>
              <input
                ref={screenshotInput}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) addScreenshots(e.target.files);
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                onClick={() => screenshotInput.current?.click()}
                className="w-full text-xs rounded-lg bg-white border border-[#DDDDD8] px-3 py-2 hover:bg-[#F0F7F8]"
              >
                Choose images or paste (Ctrl+V)
              </button>
              {screenshotPreviews.length ? (
                <div className="mt-2 grid grid-cols-3 gap-1.5">
                  {screenshotPreviews.map((src, index) => (
                    <div key={src} className="relative">
                      <img src={src} alt="" className="h-16 w-full object-cover rounded-md" />
                      <button
                        type="button"
                        onClick={() =>
                          setScreenshots((prev) => prev.filter((_, i) => i !== index))
                        }
                        className="absolute -top-1 -right-1 bg-black/70 text-white rounded-full w-4 h-4 text-[10px]"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="rounded-xl border border-dashed border-[#DDDDD8] bg-[#F8F8F6] p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-[#4A4A48] inline-flex items-center gap-1.5">
                  <FaVideo className="text-[#2fa9bf]" /> Video
                </span>
                <span className="text-[11px] text-[#8A8A85]">
                  {videos.length}/{MAX_VIDEOS} · max 32MB
                </span>
              </div>
              <input
                ref={videoInput}
                type="file"
                accept="video/*"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) addVideos(e.target.files);
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                onClick={() => videoInput.current?.click()}
                className="w-full text-xs rounded-lg bg-white border border-[#DDDDD8] px-3 py-2 hover:bg-[#F0F7F8]"
              >
                Choose a short video
              </button>
              {videos.length ? (
                <div className="mt-2 space-y-1">
                  {videos.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="flex items-center justify-between text-[11px] bg-white rounded-md px-2 py-1 border border-[#E8E8E4]"
                    >
                      <span className="truncate mr-2">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => setVideos((prev) => prev.filter((_, i) => i !== index))}
                        className="text-red-500"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-[#E8E8E4]">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 rounded-xl border border-[#DDDDD8] bg-white text-[#4A4A48] hover:bg-[#F8F8F6] text-xs font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !message.trim()}
            className="px-4 py-2 rounded-xl bg-[#2fa9bf] hover:bg-[#2296aa] text-white text-xs font-medium disabled:opacity-50"
          >
            {submitting ? "Sharing…" : "Share feedback"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewSubmitModal;
